import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  CreditCard,
  DollarSign,
  CheckCircle2,
  XCircle,
  AlertCircle,
  Clock,
  Eye,
  ArrowLeft,
  Filter,
  Search,
  ExternalLink,
  Shield,
  FileText,
  User,
  X,
  MessageSquare,
  HelpCircle,
} from 'lucide-react';
import Navbar from '../../components/feature/Navbar';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../contexts/AuthContext';
import { sendEmail } from '../../lib/emailService';
import type { Payment, Profile, Invoice } from '../../types/database';

export default function PaymentVerificationPage() {
  const { user, profile, isStaff, isAdmin } = useAuth();
  const navigate = useNavigate();

  const [payments, setPayments] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeFilter, setActiveFilter] = useState<string>('all');
  const [searchQuery, setSearchQuery] = useState('');

  // Modals
  const [selectedReceiptUrl, setSelectedReceiptUrl] = useState<string | null>(null);

  const [actionPayment, setActionPayment] = useState<any | null>(null);
  const [actionType, setActionType] = useState<'approve' | 'reject' | 'info' | null>(null);
  const [actionReason, setActionReason] = useState('');
  const [processingAction, setProcessingAction] = useState(false);
  const [actionError, setActionError] = useState('');

  useEffect(() => {
    if (!user) {
      navigate('/login');
      return;
    }
    fetchPayments();
  }, [user]);

  const fetchPayments = async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('payments')
        .select('*, client:profiles(*), invoice:invoices(*), service:services(*), course:courses(*)')
        .order('created_at', { ascending: false });

      if (!error && data) {
        setPayments(data);
      }
    } catch (err) {
      console.error('Error fetching payments:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleOpenAction = (payment: any, type: 'approve' | 'reject' | 'info') => {
    setActionPayment(payment);
    setActionType(type);
    setActionReason('');
    setActionError('');
  };

  const handleConfirmAction = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!actionPayment || !actionType || !user) return;

    if ((actionType === 'reject' || actionType === 'info') && !actionReason.trim()) {
      setActionError('A specific reason or instruction is mandatory.');
      return;
    }

    setProcessingAction(true);
    setActionError('');

    try {
      const previousStatus = actionPayment.status;
      let newStatus = '';
      let reviewDecision = '';

      if (actionType === 'approve') {
        newStatus = 'Approved';
        reviewDecision = 'Approved';
      } else if (actionType === 'reject') {
        newStatus = 'Rejected';
        reviewDecision = 'Rejected';
      } else {
        newStatus = 'More Information Required';
        reviewDecision = 'More Information Required';
      }

      // 1. Update Payment record
      const { error: payErr } = await supabase
        .from('payments')
        .update({
          status: newStatus,
          reviewer_id: user.id,
          reviewed_at: new Date().toISOString(),
          rejection_reason: actionReason.trim() || null,
          review_decision: reviewDecision,
          verified_at: newStatus === 'Approved' ? new Date().toISOString() : null,
        })
        .eq('id', actionPayment.id);

      if (payErr) throw payErr;

      // 2. Insert Audit Record in payment_verifications
      await supabase.from('payment_verifications').insert({
        payment_id: actionPayment.id,
        reviewer_id: user.id,
        reviewer_email: user.email || '',
        reviewer_name: profile?.full_name || 'Finance Officer',
        decision: reviewDecision,
        previous_status: previousStatus,
        new_status: newStatus,
        reason: actionReason.trim(),
      });

      // 3. If Approved, update corresponding Invoice, Course, or Project
      if (newStatus === 'Approved') {
        const approvedAmount = Number(actionPayment.submitted_amount || actionPayment.amount);

        // Update Invoice
        if (actionPayment.invoice_id) {
          const { data: inv } = await supabase
            .from('invoices')
            .select('*')
            .eq('id', actionPayment.invoice_id)
            .single();

          if (inv) {
            const newPaid = Number(inv.amount_paid || 0) + approvedAmount;
            const newBalance = Math.max(0, Number(inv.total_amount) - newPaid);
            const invoiceStatus = newBalance <= 0 ? 'Paid' : 'Partially Paid';

            await supabase
              .from('invoices')
              .update({
                amount_paid: newPaid,
                balance_due: newBalance,
                status: invoiceStatus,
                updated_at: new Date().toISOString(),
              })
              .eq('id', actionPayment.invoice_id);

            // Also check if linked project should be set to 'In Progress'
            if (inv.project_id) {
              await supabase
                .from('projects')
                .update({ status: 'In Progress', updated_at: new Date().toISOString() })
                .eq('id', inv.project_id);
            }
          }
        }

        // Activate Course Enrollment if applicable
        if (actionPayment.course_id && actionPayment.client_id) {
          await supabase.from('enrollments').upsert({
            user_id: actionPayment.client_id,
            course_id: actionPayment.course_id,
            status: 'Active',
            payment_status: 'Paid',
            enrolled_at: new Date().toISOString(),
          });
        }
      }

      // 4. Send Transactional Notification Email
      const clientEmail = actionPayment.client?.email;
      if (clientEmail) {
        if (newStatus === 'Approved') {
          await sendEmail({
            recipient: clientEmail,
            recipientName: actionPayment.client?.full_name,
            subject: 'Payment Verified & Approved — BientoMart',
            templateType: 'payment_approved',
            referenceNumber: actionPayment.payment_reference,
            details: {
              amount: actionPayment.submitted_amount || actionPayment.amount,
              currency: actionPayment.currency,
            },
          });
        } else if (newStatus === 'Rejected') {
          await sendEmail({
            recipient: clientEmail,
            recipientName: actionPayment.client?.full_name,
            subject: 'Payment Verification Notice — BientoMart',
            templateType: 'payment_rejected',
            referenceNumber: actionPayment.payment_reference,
            details: { reason: actionReason.trim() },
          });
        } else {
          await sendEmail({
            recipient: clientEmail,
            recipientName: actionPayment.client?.full_name,
            subject: 'Additional Information Required for Payment — BientoMart',
            templateType: 'more_info_requested',
            referenceNumber: actionPayment.payment_reference,
            details: { reason: actionReason.trim() },
          });
        }
      }

      setActionPayment(null);
      setActionType(null);
      await fetchPayments();
    } catch (err: any) {
      console.error('Payment verification action error:', err);
      setActionError(err.message || 'Action failed.');
    } finally {
      setProcessingAction(false);
    }
  };

  const filteredPayments = payments.filter((p) => {
    const matchesFilter =
      activeFilter === 'all' ||
      (activeFilter === 'review' && p.status === 'Payment Under Review') ||
      (activeFilter === 'approved' && p.status === 'Approved') ||
      (activeFilter === 'rejected' && p.status === 'Rejected') ||
      (activeFilter === 'info' && p.status === 'More Information Required');

    const q = searchQuery.toLowerCase();
    const matchesSearch =
      !q ||
      p.payment_reference?.toLowerCase().includes(q) ||
      p.provider_tx_id?.toLowerCase().includes(q) ||
      p.client?.full_name?.toLowerCase().includes(q) ||
      p.client?.email?.toLowerCase().includes(q) ||
      p.payment_method?.toLowerCase().includes(q);

    return matchesFilter && matchesSearch;
  });

  const pendingCount = payments.filter((p) => p.status === 'Payment Under Review').length;

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col pt-20">
      <Navbar />

      <main className="flex-1 max-w-7xl w-full mx-auto px-4 lg:px-8 py-8">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
          <div>
            <div className="flex items-center gap-2 mb-2">
              <Link
                to="/admin"
                className="text-xs font-semibold text-gray-500 hover:text-darkBlue-900 flex items-center gap-1"
              >
                <ArrowLeft className="w-3.5 h-3.5" />
                <span>Back to Admin Console</span>
              </Link>
              <span className="text-gray-300">•</span>
              <Link
                to="/admin/payment-instructions"
                className="text-xs font-semibold text-clairBlue-600 hover:underline"
              >
                Manage Payment Channels →
              </Link>
            </div>
            <h1 className="text-2xl sm:text-3xl font-black text-darkBlue-900 flex items-center gap-2">
              <span>Payment Verification Desk</span>
              {pendingCount > 0 && (
                <span className="px-2.5 py-0.5 rounded-full text-xs font-bold bg-amber-500 text-white animate-pulse">
                  {pendingCount} Pending Review
                </span>
              )}
            </h1>
            <p className="text-xs sm:text-sm text-gray-500 mt-1">
              Verify submitted bank slips and Mobile Money transaction proofs. Authorized finance staff manually approves settlements.
            </p>
          </div>

          <div className="flex items-center gap-2">
            <button
              onClick={fetchPayments}
              className="px-4 py-2 bg-white border border-gray-200 hover:bg-gray-50 text-gray-700 rounded-xl text-xs font-bold shadow-sm transition-all"
            >
              Refresh Table
            </button>
          </div>
        </div>

        {/* Filters and Search Bar */}
        <div className="bg-white p-4 rounded-2xl border border-gray-200 mb-6 flex flex-col md:flex-row items-center justify-between gap-3 shadow-sm">
          <div className="flex items-center gap-1.5 overflow-x-auto w-full md:w-auto pb-1 md:pb-0 scrollbar-none">
            {[
              { id: 'all', label: 'All Submissions' },
              { id: 'review', label: 'Under Review', count: pendingCount },
              { id: 'approved', label: 'Approved' },
              { id: 'rejected', label: 'Rejected' },
              { id: 'info', label: 'More Info Needed' },
            ].map((f) => (
              <button
                key={f.id}
                onClick={() => setActiveFilter(f.id)}
                className={`px-3 py-1.5 rounded-xl text-xs font-bold transition-all whitespace-nowrap cursor-pointer flex items-center gap-1.5 ${
                  activeFilter === f.id
                    ? 'bg-darkBlue-900 text-white shadow-sm'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                <span>{f.label}</span>
                {f.count !== undefined && f.count > 0 && (
                  <span className="px-1.5 py-0.2 rounded-full text-[10px] bg-amber-500 text-white">
                    {f.count}
                  </span>
                )}
              </button>
            ))}
          </div>

          <div className="relative w-full md:w-64">
            <Search className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
            <input
              type="text"
              placeholder="Search reference, client..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-9 pr-3 py-1.5 bg-gray-50 border border-gray-200 rounded-xl text-xs focus:outline-none focus:ring-2 focus:ring-clairBlue-500"
            />
          </div>
        </div>

        {/* Payments Table */}
        <div className="bg-white rounded-3xl border border-gray-200 overflow-hidden shadow-sm">
          {loading ? (
            <div className="text-center py-20">
              <div className="w-10 h-10 border-4 border-clairBlue-500 border-t-transparent rounded-full animate-spin mx-auto mb-3" />
              <p className="text-xs text-gray-400 font-semibold">Loading payment audit log...</p>
            </div>
          ) : filteredPayments.length === 0 ? (
            <div className="text-center py-16 text-gray-400 text-xs">
              No payment records match the current filter.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-left text-xs">
                <thead className="bg-gray-50 border-b border-gray-200 text-gray-500 uppercase tracking-wider font-semibold">
                  <tr>
                    <th className="px-5 py-3.5">Payment Ref / Tx ID</th>
                    <th className="px-5 py-3.5">Customer</th>
                    <th className="px-5 py-3.5">Linked Item</th>
                    <th className="px-5 py-3.5">Amount (Submitted / Exp)</th>
                    <th className="px-5 py-3.5">Channel / Method</th>
                    <th className="px-5 py-3.5">Payment Date</th>
                    <th className="px-5 py-3.5">Receipt Proof</th>
                    <th className="px-5 py-3.5">Status</th>
                    <th className="px-5 py-3.5 text-right">Verification Action</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100 text-gray-700 font-medium">
                  {filteredPayments.map((p) => {
                    const isUnderReview = p.status === 'Payment Under Review';

                    return (
                      <tr key={p.id} className="hover:bg-gray-50/80 transition-colors">
                        <td className="px-5 py-4">
                          <span className="font-mono font-bold text-darkBlue-900 block">
                            {p.payment_reference}
                          </span>
                          {p.provider_tx_id && (
                            <span className="text-[11px] text-gray-400 font-mono">
                              Tx: {p.provider_tx_id}
                            </span>
                          )}
                        </td>
                        <td className="px-5 py-4">
                          <p className="font-bold text-gray-900">{p.client?.full_name || 'Guest Customer'}</p>
                          <p className="text-[11px] text-gray-400">{p.client?.email || p.metadata?.customer_email}</p>
                        </td>
                        <td className="px-5 py-4">
                          {p.invoice ? (
                            <span className="font-semibold text-clairBlue-600 block">
                              Invoice #{p.invoice.invoice_number}
                            </span>
                          ) : p.course ? (
                            <span className="font-semibold text-purple-600 block line-clamp-1">
                              Course: {p.course.title}
                            </span>
                          ) : p.service ? (
                            <span className="font-semibold text-gray-700 block line-clamp-1">
                              Service: {p.service.title}
                            </span>
                          ) : (
                            <span className="text-gray-400">—</span>
                          )}
                        </td>
                        <td className="px-5 py-4">
                          <span className="font-black text-darkBlue-900 block">
                            ${Number(p.submitted_amount || p.amount).toFixed(2)} {p.currency}
                          </span>
                          {p.expected_amount && (
                            <span className="text-[10px] text-gray-400">
                              Exp: ${Number(p.expected_amount).toFixed(2)}
                            </span>
                          )}
                        </td>
                        <td className="px-5 py-4">
                          <span className="px-2 py-0.5 rounded-lg bg-gray-100 text-gray-700 font-semibold text-[11px]">
                            {p.payment_method || p.provider}
                          </span>
                        </td>
                        <td className="px-5 py-4 text-gray-500">
                          {p.payment_date ? new Date(p.payment_date).toLocaleDateString() : new Date(p.created_at).toLocaleDateString()}
                        </td>
                        <td className="px-5 py-4">
                          {p.receipt_url ? (
                            <button
                              onClick={() => setSelectedReceiptUrl(p.receipt_url)}
                              className="px-2.5 py-1 bg-clairBlue-50 hover:bg-clairBlue-100 text-clairBlue-700 rounded-lg text-xs font-bold flex items-center gap-1 transition-colors"
                            >
                              <Eye className="w-3.5 h-3.5" />
                              <span>View Receipt</span>
                            </button>
                          ) : (
                            <span className="text-[11px] text-gray-400 italic">No file attached</span>
                          )}
                        </td>
                        <td className="px-5 py-4">
                          <span
                            className={`px-2.5 py-0.5 rounded-full text-[10px] font-bold ${
                              p.status === 'Approved'
                                ? 'bg-emerald-50 text-emerald-700 border border-emerald-200'
                                : p.status === 'Payment Under Review'
                                ? 'bg-amber-50 text-amber-700 border border-amber-200'
                                : p.status === 'Rejected'
                                ? 'bg-red-50 text-red-700 border border-red-200'
                                : 'bg-blue-50 text-blue-700 border border-blue-200'
                            }`}
                          >
                            {p.status}
                          </span>
                        </td>
                        <td className="px-5 py-4 text-right">
                          <div className="flex items-center justify-end gap-1.5">
                            {p.status !== 'Approved' && (
                              <button
                                onClick={() => handleOpenAction(p, 'approve')}
                                className="px-2.5 py-1 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg text-xs font-bold transition-colors shadow-sm"
                                title="Approve Verified Payment"
                              >
                                Approve
                              </button>
                            )}

                            {p.status !== 'Rejected' && (
                              <button
                                onClick={() => handleOpenAction(p, 'reject')}
                                className="px-2.5 py-1 bg-red-600 hover:bg-red-700 text-white rounded-lg text-xs font-bold transition-colors shadow-sm"
                                title="Reject Invalid Submission"
                              >
                                Reject
                              </button>
                            )}

                            <button
                              onClick={() => handleOpenAction(p, 'info')}
                              className="px-2.5 py-1 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg text-xs font-bold transition-colors"
                              title="Request Additional Details"
                            >
                              More Info
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Receipt Image / PDF Viewer Modal */}
        {selectedReceiptUrl && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/75 backdrop-blur-sm">
            <div className="bg-white rounded-3xl shadow-2xl max-w-3xl w-full p-6 border border-gray-100 max-h-[90vh] flex flex-col">
              <div className="flex items-center justify-between pb-3 border-b border-gray-100 mb-4">
                <h3 className="text-base font-bold text-darkBlue-900 flex items-center gap-2">
                  <FileText className="w-4 h-4 text-clairBlue-600" />
                  <span>Customer Uploaded Payment Proof</span>
                </h3>
                <div className="flex items-center gap-2">
                  <a
                    href={selectedReceiptUrl}
                    target="_blank"
                    rel="noreferrer"
                    className="px-3 py-1 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg text-xs font-semibold flex items-center gap-1"
                  >
                    <ExternalLink className="w-3.5 h-3.5" />
                    <span>Open Full Screen</span>
                  </a>
                  <button
                    onClick={() => setSelectedReceiptUrl(null)}
                    className="w-8 h-8 rounded-full bg-gray-100 hover:bg-gray-200 flex items-center justify-center"
                  >
                    <X className="w-4 h-4 text-gray-600" />
                  </button>
                </div>
              </div>

              <div className="flex-1 overflow-auto bg-gray-50 rounded-2xl p-4 flex items-center justify-center min-h-[300px]">
                {selectedReceiptUrl.endsWith('.pdf') ? (
                  <iframe src={selectedReceiptUrl} className="w-full h-[500px] rounded-xl border border-gray-200" title="Receipt PDF" />
                ) : (
                  <img src={selectedReceiptUrl} alt="Receipt proof" className="max-w-full max-h-[500px] object-contain rounded-xl shadow-sm" />
                )}
              </div>
            </div>
          </div>
        )}

        {/* Action Modal (Approve, Reject, More Info) */}
        {actionPayment && actionType && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
            <div className="bg-white rounded-3xl shadow-2xl max-w-md w-full p-6 sm:p-8 border border-gray-100">
              <div className="flex items-center justify-between pb-3 border-b border-gray-100 mb-4">
                <h3 className="text-lg font-bold text-darkBlue-900">
                  {actionType === 'approve'
                    ? 'Confirm Payment Approval'
                    : actionType === 'reject'
                    ? 'Reject Payment Submission'
                    : 'Request Additional Details'}
                </h3>
                <button
                  onClick={() => {
                    setActionPayment(null);
                    setActionType(null);
                  }}
                  className="w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center"
                >
                  <X className="w-4 h-4 text-gray-600" />
                </button>
              </div>

              <form onSubmit={handleConfirmAction} className="space-y-4 text-xs">
                <div className="p-3 bg-gray-50 rounded-2xl border border-gray-100 space-y-1">
                  <div>
                    <span className="text-gray-400">Payment Ref:</span>{' '}
                    <strong className="text-darkBlue-900">{actionPayment.payment_reference}</strong>
                  </div>
                  <div>
                    <span className="text-gray-400">Customer:</span>{' '}
                    <span className="font-semibold text-gray-800">{actionPayment.client?.full_name || 'Client'}</span>
                  </div>
                  <div>
                    <span className="text-gray-400">Amount:</span>{' '}
                    <strong className="text-emerald-700">
                      ${Number(actionPayment.submitted_amount || actionPayment.amount).toFixed(2)} {actionPayment.currency}
                    </strong>
                  </div>
                </div>

                {actionType === 'approve' && (
                  <p className="text-gray-600 leading-relaxed">
                    By approving, you certify that the funds have been cleared into BientoMart's bank account or Mobile Money line. This will immediately update the invoice and activate the service or course.
                  </p>
                )}

                {(actionType === 'reject' || actionType === 'info') && (
                  <div>
                    <label className="block font-bold text-gray-700 mb-1">
                      {actionType === 'reject' ? 'Rejection Reason *' : 'Information Required *'}
                    </label>
                    <textarea
                      rows={3}
                      required
                      value={actionReason}
                      onChange={(e) => setActionReason(e.target.value)}
                      placeholder={
                        actionType === 'reject'
                          ? 'Explain why this payment cannot be approved (e.g. invalid reference, funds not received)...'
                          : 'Describe specifically what the client needs to re-upload (e.g. full bank deposit slip)...'
                      }
                      className="w-full px-3 py-2 bg-gray-50 border border-gray-200 rounded-xl resize-none focus:outline-none focus:ring-2 focus:ring-clairBlue-500"
                    />
                  </div>
                )}

                {actionError && (
                  <div className="p-2.5 bg-red-50 text-red-700 border border-red-200 rounded-xl text-xs">
                    {actionError}
                  </div>
                )}

                <div className="flex justify-end gap-3 pt-3 border-t border-gray-100">
                  <button
                    type="button"
                    onClick={() => {
                      setActionPayment(null);
                      setActionType(null);
                    }}
                    className="px-4 py-2 font-semibold text-gray-500"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={processingAction}
                    className={`px-5 py-2.5 rounded-xl font-bold text-white shadow-md transition-all ${
                      actionType === 'approve'
                        ? 'bg-emerald-600 hover:bg-emerald-700'
                        : actionType === 'reject'
                        ? 'bg-red-600 hover:bg-red-700'
                        : 'bg-darkBlue-900 hover:bg-darkBlue-800'
                    }`}
                  >
                    {processingAction ? 'Submitting...' : 'Confirm Decision'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}
